import React, { useState } from 'react';
import { Calendar, MessageSquare, Eye, CheckCircle, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// Mock data for demonstration
const mockBookings = [
  {
    id: 1,
    partner1: 'Sarah Johnson',
    partner2: 'Michael Smith',
    email: 'sarah.michael@email.com',
    phone: '(555) 123-4567',
    eventDate: '2025-06-15',
    eventTime: '14:00',
    location: 'Biltmore Estate, Asheville, NC',
    guestCount: '76-150',
    serviceType: 'wedding',
    package: 'signature',
    message: 'We would love a ceremony that blends traditional elements with our own personal touches. We met in college and have been together for 8 years.',
    status: 'new',
    createdAt: '2025-01-15T10:30:00Z'
  },
  {
    id: 2,
    partner1: 'James Wilson',
    partner2: 'David Chen',
    email: 'jw.dc@email.com',
    phone: '(555) 987-6543',
    eventDate: '2025-04-20',
    eventTime: '11:00',
    location: 'JC Raulston Arboretum, Raleigh, NC',
    guestCount: '31-75',
    serviceType: 'commitment',
    package: 'premium',
    message: 'Looking for an inclusive ceremony that celebrates our 10-year journey together.',
    status: 'contacted',
    createdAt: '2025-01-14T15:45:00Z'
  },
  {
    id: 3,
    partner1: 'Emily Rose',
    partner2: 'Robert Taylor',
    email: 'emily.robert2025@email.com',
    phone: '(555) 456-7890',
    eventDate: '2025-03-01',
    eventTime: '16:00',
    location: 'Grandfather Mountain, NC',
    guestCount: '1-10',
    serviceType: 'elopement',
    package: 'essential',
    message: 'Simple elopement at sunset. Just the two of us.',
    status: 'confirmed',
    createdAt: '2025-01-10T09:00:00Z'
  }
];

const mockContacts = [
  {
    id: 1,
    name: 'Amanda Peters',
    email: 'amanda.p@email.com',
    subject: 'Availability Question',
    message: 'Do you have availability for a Sunday ceremony in May 2025?',
    status: 'unread',
    createdAt: '2025-01-15T14:20:00Z'
  },
  {
    id: 2,
    name: 'Thomas Green',
    email: 'tgreen@email.com',
    subject: 'Vow Renewal Inquiry',
    message: 'My wife and I are celebrating 25 years and want to renew our vows. What packages do you recommend?',
    status: 'read',
    createdAt: '2025-01-14T11:30:00Z'
  }
];

const Analytics = () => {
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [bookings] = useState(mockBookings);
  const [contacts] = useState(mockContacts);

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
      case 'unread':
        return { bg: 'rgba(184,134,11,0.2)', text: '#B8860B' };
      case 'contacted':
        return { bg: 'rgba(59,130,246,0.2)', text: '#3b82f6' };
      case 'confirmed':
        return { bg: 'rgba(34,197,94,0.2)', text: '#22c55e' };
      case 'read':
        return { bg: 'rgba(107,114,128,0.2)', text: '#6b7280' };
      default:
        return { bg: 'rgba(107,114,128,0.2)', text: '#6b7280' };
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Dashboard
  const stats = {
    totalBookings: bookings.length,
    newBookings: bookings.filter(b => b.status === 'new').length,
    confirmedBookings: bookings.filter(b => b.status === 'confirmed').length,
    unreadMessages: contacts.filter(c => c.status === 'unread').length
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#1a1a1a', paddingTop: '2rem' }} data-testid="analytics-dashboard">
      {/* Header */}
      <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '0 1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <h1 className="font-serif" style={{ fontSize: '2rem', color: '#F5F5DC', marginBottom: '0.25rem' }}>
              Admin Dashboard
            </h1>
            <p style={{ color: 'rgba(245,245,220,0.6)', fontSize: '0.875rem' }}>
              Drip N Fiyah Ministry
            </p>
          </div>
          <button
            onClick={logout}
            data-testid="analytics-logout-btn"
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: 'transparent',
              border: '1px solid rgba(184,134,11,0.3)',
              color: '#B8860B',
              borderRadius: '2px',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            Logout
          </button>
        </div>

        {/* Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          {[
            { label: 'Total Bookings', value: stats.totalBookings, icon: Calendar, color: '#B8860B' },
            { label: 'New Requests', value: stats.newBookings, icon: Clock, color: '#f59e0b' },
            { label: 'Confirmed', value: stats.confirmedBookings, icon: CheckCircle, color: '#22c55e' },
            { label: 'Unread Messages', value: stats.unreadMessages, icon: MessageSquare, color: '#3b82f6' }
          ].map((stat, idx) => {
            const IconComponent = stat.icon;
            return (
              <div key={idx} className="card-elegant" style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <p style={{ color: 'rgba(245,245,220,0.6)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>
                      {stat.label}
                    </p>
                    <p style={{ color: '#F5F5DC', fontSize: '2rem', fontWeight: 600 }}>
                      {stat.value}
                    </p>
                  </div>
                  <IconComponent style={{ width: '2rem', height: '2rem', color: stat.color, opacity: 0.7 }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '1rem', borderBottom: '1px solid rgba(184,134,11,0.2)', marginBottom: '2rem' }}>
          {[
            { id: 'overview', label: 'Overview', icon: Eye },
            { id: 'bookings', label: 'Bookings', icon: Calendar },
            { id: 'messages', label: 'Messages', icon: MessageSquare }
          ].map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '1rem 1.5rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: activeTab === tab.id ? '#B8860B' : 'rgba(245,245,220,0.6)',
                  borderBottom: activeTab === tab.id ? '2px solid #B8860B' : '2px solid transparent',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  marginBottom: '-1px'
                }}
              >
                <IconComponent style={{ width: '1rem', height: '1rem' }} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            {/* Recent Bookings */}
            <div className="card-elegant" style={{ padding: '1.5rem' }}>
              <h3 className="font-serif" style={{ fontSize: '1.25rem', color: '#F5F5DC', marginBottom: '1rem' }}>
                Recent Booking Requests
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {bookings.slice(0, 3).map((booking) => (
                  <div key={booking.id} style={{ padding: '1rem', backgroundColor: 'rgba(26,26,26,0.5)', borderRadius: '4px', border: '1px solid rgba(184,134,11,0.1)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                      <p style={{ color: '#F5F5DC', fontWeight: 500 }}>
                        {booking.partner1} & {booking.partner2}
                      </p>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '999px',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        backgroundColor: getStatusColor(booking.status).bg,
                        color: getStatusColor(booking.status).text
                      }}>
                        {booking.status}
                      </span>
                    </div>
                    <p style={{ color: 'rgba(245,245,220,0.6)', fontSize: '0.875rem' }}>
                      {booking.serviceType} • {formatDate(booking.eventDate)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Messages */}
            <div className="card-elegant" style={{ padding: '1.5rem' }}>
              <h3 className="font-serif" style={{ fontSize: '1.25rem', color: '#F5F5DC', marginBottom: '1rem' }}>
                Recent Messages
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {contacts.map((contact) => (
                  <div key={contact.id} style={{ padding: '1rem', backgroundColor: 'rgba(26,26,26,0.5)', borderRadius: '4px', border: '1px solid rgba(184,134,11,0.1)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                      <p style={{ color: '#F5F5DC', fontWeight: 500 }}>
                        {contact.name}
                      </p>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '999px',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        backgroundColor: getStatusColor(contact.status).bg,
                        color: getStatusColor(contact.status).text
                      }}>
                        {contact.status}
                      </span>
                    </div>
                    <p style={{ color: '#B8860B', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                      {contact.subject}
                    </p>
                    <p style={{ color: 'rgba(245,245,220,0.5)', fontSize: '0.75rem' }}>
                      {formatDate(contact.createdAt)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'bookings' && (
          <div className="card-elegant" style={{ padding: '1.5rem', overflowX: 'auto' }}>
            <h3 className="font-serif" style={{ fontSize: '1.25rem', color: '#F5F5DC', marginBottom: '1.5rem' }}>
              All Booking Requests
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(184,134,11,0.2)' }}>
                  {['Couple', 'Service', 'Date', 'Location', 'Package', 'Status'].map((header) => (
                    <th key={header} style={{ textAlign: 'left', padding: '1rem', color: 'rgba(245,245,220,0.6)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking) => (
                  <tr key={booking.id} style={{ borderBottom: '1px solid rgba(184,134,11,0.1)' }}>
                    <td style={{ padding: '1rem' }}>
                      <p style={{ color: '#F5F5DC', fontWeight: 500 }}>{booking.partner1} & {booking.partner2}</p>
                      <p style={{ color: 'rgba(245,245,220,0.5)', fontSize: '0.75rem' }}>{booking.email}</p>
                    </td>
                    <td style={{ padding: '1rem', color: 'rgba(245,245,220,0.8)', textTransform: 'capitalize' }}>{booking.serviceType}</td>
                    <td style={{ padding: '1rem', color: 'rgba(245,245,220,0.8)' }}>{formatDate(booking.eventDate)}</td>
                    <td style={{ padding: '1rem', color: 'rgba(245,245,220,0.6)', fontSize: '0.875rem' }}>{booking.location}</td>
                    <td style={{ padding: '1rem', color: '#B8860B', textTransform: 'capitalize' }}>{booking.package}</td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '999px',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        backgroundColor: getStatusColor(booking.status).bg,
                        color: getStatusColor(booking.status).text
                      }}>
                        {booking.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="card-elegant" style={{ padding: '1.5rem' }}>
            <h3 className="font-serif" style={{ fontSize: '1.25rem', color: '#F5F5DC', marginBottom: '1.5rem' }}>
              Contact Messages
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {contacts.map((contact) => (
                <div key={contact.id} style={{ padding: '1.5rem', backgroundColor: 'rgba(26,26,26,0.5)', borderRadius: '4px', border: '1px solid rgba(184,134,11,0.1)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                    <div>
                      <p style={{ color: '#F5F5DC', fontWeight: 500, marginBottom: '0.25rem' }}>{contact.name}</p>
                      <p style={{ color: 'rgba(245,245,220,0.5)', fontSize: '0.875rem' }}>{contact.email}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '999px',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        backgroundColor: getStatusColor(contact.status).bg,
                        color: getStatusColor(contact.status).text
                      }}>
                        {contact.status}
                      </span>
                      <p style={{ color: 'rgba(245,245,220,0.4)', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                        {formatDate(contact.createdAt)}
                      </p>
                    </div>
                  </div>
                  <p style={{ color: '#B8860B', fontWeight: 500, marginBottom: '0.5rem' }}>{contact.subject}</p>
                  <p style={{ color: 'rgba(245,245,220,0.7)', lineHeight: 1.6 }}>{contact.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Footer spacing */}
      <div style={{ height: '4rem' }} />
    </div>
  );
};

export default Analytics;
